package com.videoanalyzer.web.state

import com.videoanalyzer.web.models.*

/**
 * Application state management.
 *
 * This is similar to ViewModel state in Android, but simpler.
 * We use sealed classes to represent different states.
 *
 * ANDROID EQUIVALENT:
 * Like StateFlow<UiState> in a ViewModel, but for web.
 */

// ============================================================
// Authentication State
// ============================================================

/**
 * Represents the current authentication state.
 */
sealed class AuthState {
    /** Initial state - checking if user is logged in */
    object Loading : AuthState()

    /** User is not logged in */
    object LoggedOut : AuthState()

    /** User is logged in */
    data class LoggedIn(
        val user: UserProfile,
        val analysesRemaining: Int  // -1 for unlimited (pro)
    ) : AuthState()

    /** Error during auth check */
    data class Error(val message: String) : AuthState()
}

// ============================================================
// Analysis State
// ============================================================

/**
 * Represents the state of video analysis.
 */
sealed class AnalysisState {
    /** No analysis in progress */
    object Idle : AnalysisState()

    /** Fetching video metadata */
    object LoadingVideo : AnalysisState()

    /** Video loaded, ready to analyze */
    data class VideoLoaded(val video: VideoMetadata) : AnalysisState()

    /** Analysis in progress */
    object Analyzing : AnalysisState()

    /** Analysis completed successfully */
    data class Success(
        val analysis: FullAnalysis,
        val isExisting: Boolean,
        val message: String?,
        // Gating info for freemium
        val showFullRecommendations: Boolean = true,
        val totalRecommendationsCount: Int = 0,
        val freeRecommendationsLimit: Int = 2
    ) : AnalysisState()

    /** Analysis failed */
    data class Error(val message: String) : AnalysisState()

    /** Usage limit reached */
    data class LimitReached(
        val currentUsage: Int,
        val limit: Int
    ) : AnalysisState()
}

// ============================================================
// History State
// ============================================================

/**
 * Represents the state of analysis history.
 */
sealed class HistoryState {
    object Loading : HistoryState()

    data class Loaded(
        val analyses: List<AnalysisSummary>,
        val total: Int,
        val hasMore: Boolean
    ) : HistoryState()

    data class Error(val message: String) : HistoryState()
}

// ============================================================
// Screen Navigation
// ============================================================

/**
 * Represents which screen is currently shown.
 *
 * Since Compose for Web doesn't have a navigation library,
 * we manage screens manually with state.
 */
sealed class Screen {
    /** Home/analysis screen */
    object Home : Screen()

    /** Analysis result detail */
    data class AnalysisDetail(val analysisId: String) : Screen()

    /** History list */
    object History : Screen()

    /** Settings/account */
    object Settings : Screen()

    /** Pricing/upgrade page */
    object Pricing : Screen()

    /** Contact page */
    object Contact : Screen()

    /** Terms of Service page */
    object Terms : Screen()

    /** Privacy Policy page */
    object Privacy : Screen()

    companion object {
        /** Get screen from URL path */
        fun fromPath(path: String): Screen = when (path.lowercase().trimEnd('/')) {
            "/terms" -> Terms
            "/privacy" -> Privacy
            "/pricing" -> Pricing
            "/contact" -> Contact
            "/history" -> History
            "/settings" -> Settings
            else -> Home
        }

        /** Get URL path for screen */
        fun toPath(screen: Screen): String = when (screen) {
            is Terms -> "/terms"
            is Privacy -> "/privacy"
            is Pricing -> "/pricing"
            is Contact -> "/contact"
            is History -> "/history"
            is Settings -> "/settings"
            is Home -> "/"
            is AnalysisDetail -> "/"
        }
    }
}

// ============================================================
// Form State
// ============================================================

/**
 * State for the login/signup forms.
 */
data class AuthFormState(
    val email: String = "",
    val password: String = "",
    val isLogin: Boolean = true,  // true = login, false = signup
    val isLoading: Boolean = false,
    val error: String? = null
)

/**
 * State for the URL input form.
 */
data class AnalyzerFormState(
    val url: String = "",
    val isValid: Boolean = false,
    val error: String? = null
)

// ============================================================
// UI Helpers
// ============================================================

/**
 * Get CSS class for score color.
 */
fun getScoreClass(score: Int): String = when {
    score >= 80 -> "score-excellent"
    score >= 60 -> "score-good"
    score >= 40 -> "score-average"
    else -> "score-poor"
}

/**
 * Get label for score.
 */
fun getScoreLabel(score: Int): String = when {
    score >= 80 -> "Excellent"
    score >= 60 -> "Good"
    score >= 40 -> "Average"
    else -> "Needs Work"
}

/**
 * Format large numbers (1000 -> 1K, 1000000 -> 1M).
 */
fun formatNumber(num: Long): String = when {
    num >= 1_000_000 -> "${num / 1_000_000}M"
    num >= 1_000 -> "${num / 1_000}K"
    else -> num.toString()
}

/**
 * Format date string for display.
 */
fun formatDate(isoDate: String): String {
    // Simple formatting - just show date part
    return isoDate.substringBefore("T")
}
